import { useEffect, useState, type ReactNode } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { CheckIcon, DateTimePicker, DocumentIcon, MenuButton, MoreIcon, PinIcon, PlayIcon, PlusIcon, Select, toISODate, Tooltip, TrashIcon, useConfirm, UserCheckIcon, useToast, type MenuItem } from "@signet/ui";
import { CreatorAvatar, useCreatorPhotos, useMe } from "@signet/web-kit";
import { BASE, createTodo, deleteTodo, dueLabel, fetchCreators, fetchPeople, moveTodo, PRIORITIES, PRIORITY_LABEL, todoKeys, updateTodo, type Todo, type TodoPatch } from "./api.ts";
import type { Place, useSortable } from "./sortable.ts";
import { NoteChips } from "./notes.tsx";
import { useShiftHeld } from "./shift.ts";
import { useTelegram, useTick, useWorkOn } from "./telegram.tsx";
import { TickBurst, TickMark, useLeave } from "./tick.tsx";

/* ──────────────────────────────────────────────────────────────────────────
   A to-do in a list, as one line, and the box that adds one. Shared by the To-do page and
   the panel on Now, so the two look and behave the same.
   ────────────────────────────────────────────────────────────────────────── */

const messageOf = (error: unknown) => (error instanceof Error ? error.message : "Something went wrong.");

export function useTodoUpdate() {
  const client = useQueryClient();
  const toast = useToast();
  return useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: TodoPatch }) => updateTodo(id, patch),
    onSuccess: () => client.invalidateQueries({ queryKey: todoKeys.all }),
    onError: (error) => toast.error("Couldn't save that", messageOf(error)),
  });
}

/** With no `assignee`, the server gives the new to-do to whoever added it. */
/**
 * Saves a drag. The list on screen is reordered at once (the drag hands over
 * in the same frame), then the server, then every list is read again.
 */
export function useMoveTodo(queryKey: readonly unknown[]) {
  const client = useQueryClient();
  const toast = useToast();
  return (id: string, place: Place) => {
    client.setQueryData<{ items: Todo[] }>(queryKey, (old) => {
      if (!old) return old;
      const moving = old.items.find((t) => t.id === id);
      if (!moving) return old;
      const rest = old.items.filter((t) => t.id !== id);
      const anchor = rest.findIndex((t) => t.id === ("before" in place ? place.before : place.after));
      if (anchor < 0) return old;
      rest.splice("before" in place ? anchor : anchor + 1, 0, moving);
      return { ...old, items: rest };
    });
    moveTodo(id, place)
      .catch((error) => toast.error("Couldn't move it", messageOf(error)))
      .finally(() => void client.invalidateQueries({ queryKey: todoKeys.all }));
  };
}

export function QuickAdd({ autoFocus = false, assignee }: { autoFocus?: boolean; assignee?: string }) {
  const [name, setName] = useState("");
  const { can } = useMe();
  const client = useQueryClient();
  const toast = useToast();
  const add = useMutation({
    mutationFn: () => createTodo({ name: name.trim(), ...(assignee ? { assignee } : {}) }),
    onSuccess: () => {
      setName("");
      void client.invalidateQueries({ queryKey: todoKeys.all });
    },
    onError: (error) => toast.error("Couldn't add it", messageOf(error)),
  });
  if (!can("todo.item:create")) return null;
  return (
    <form
      className="td-add"
      onSubmit={(e) => {
        e.preventDefault();
        if (name.trim() && !add.isPending) add.mutate();
      }}
    >
      <PlusIcon size={16} />
      <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Add a to-do and press Enter" aria-label="New to-do" autoFocus={autoFocus} maxLength={300} />
      {name.trim() ? (
        <button type="submit" className="button brass compact" disabled={add.isPending}>
          Add
        </button>
      ) : null}
    </form>
  );
}

/**
 * Everyone a to-do can be given to, and every creator on the roster, read once
 * and shared by all rows. Creators come from the server's roster, not the photo
 * index, which still has a face for everyone who has left.
 */
export function useTodoChoices(enabled = true) {
  const people = useQuery({ queryKey: todoKeys.people, queryFn: fetchPeople, staleTime: 5 * 60_000, enabled });
  const roster = useQuery({ queryKey: todoKeys.creators, queryFn: fetchCreators, staleTime: 60_000, retry: false, enabled });
  return { people: people.data?.people ?? [], creators: roster.data?.creators ?? [] };
}

/**
 * One to-do as a single line (Ben, 2026-10-08), editable right there (Jay,
 * 2026-10-08): tick box, creator, who it is for, priority, due date, and a
 * menu. The name opens the panel, where it and the notes are edited.
 */
export function TodoRow({ todo, onOpen, showNotes = true, sort }: { todo: Todo; onOpen: () => void; showNotes?: boolean; sort?: ReturnType<typeof useSortable> }) {
  const update = useTodoUpdate();
  const client = useQueryClient();
  const toast = useToast();
  const { confirm, element } = useConfirm();
  const photos = useCreatorPhotos(BASE);
  const { me, can } = useMe();
  const choices = useTodoChoices();
  const editable = can("todo.item:edit");
  const save = (patch: TodoPatch) => update.mutate({ id: todo.id, patch });
  const due = todo.dueOn ? dueLabel(todo.dueOn) : null;
  // Jay's Telegram (2026-10-08): ticking the one he is on asks what is next; Work on this switches.
  const telegram = useTelegram();
  const leave = useLeave<HTMLLIElement>();
  const ticking = useTick(todo, leave);
  const work = useWorkOn();
  const mineNow = telegram?.working === todo.id;
  const workingLabel = todo.done || !todo.workingBy.length ? null : mineNow ? "Working on it" : `${todo.workingBy.join(" and ")} ${todo.workingBy.length > 1 ? "are" : "is"} currently working on this`;

  const creatorOptions = [...new Set([...choices.creators, ...(todo.creator ? [todo.creator] : [])])].map((c) => ({ value: c, label: c, image: photos(c) }));
  const personOptions = choices.people.map((p) => ({ value: p.id, label: p.name }));
  const priorityOptions = PRIORITIES.map((p) => ({ value: p, label: PRIORITY_LABEL[p] }));

  const shift = useShiftHeld();
  // The row's actions, once: the ⋯ menu lists them, and with Shift held they sit on the row as buttons.
  const actions: { key: string; label: string; icon: ReactNode; run: () => void; danger?: boolean }[] = [
    { key: "notes", label: todo.notes ? "Notes and details" : "Add notes", icon: <DocumentIcon size={15} />, run: onOpen },
    ...(editable ? [{ key: "pin", label: todo.pinned ? "Unpin" : "Pin to the top", icon: <PinIcon size={15} />, run: () => save({ pinned: !todo.pinned }) }] : []),
    ...(editable && me && todo.assignee?.id !== me.user.id ? [{ key: "mine", label: "Give it to me", icon: <UserCheckIcon size={15} />, run: () => save({ assignee: me.user.id }) }] : []),
    ...(can("todo.item:delete")
      ? [
          {
            key: "delete",
            label: "Delete",
            icon: <TrashIcon size={15} />,
            danger: true,
            run: async () => {
              if (!(await confirm({ title: `Delete "${todo.name}"?`, body: "It's gone for everyone. To keep it, tick it off instead.", confirmLabel: "Delete", danger: true, enterConfirms: true }))) return;
              try {
                await deleteTodo(todo.id);
                void client.invalidateQueries({ queryKey: todoKeys.all });
              } catch (error) {
                toast.error("Couldn't delete it", messageOf(error));
              }
            },
          },
        ]
      : []),
  ];
  const menu: MenuItem[] = actions.flatMap((a): MenuItem[] => [
    ...(a.danger ? [{ kind: "divider" } as MenuItem] : []),
    { kind: "item", label: a.label, icon: a.icon, danger: a.danger, onSelect: a.run },
  ]);

  return (
    <li
      ref={leave.ref}
      className={`td-row${todo.done ? " done" : ""}${leave.active ? " td-ticking" : ""}${todo.pinned ? " pinned" : ""}${telegram ? " has-work" : ""}${workingLabel ? " working" : ""}`}
      // Anywhere on the line opens the panel (Jay, 2026-10-08), except its own
      // controls and the pop-ups they open, which React bubbles up to here.
      {...sort?.rowProps(todo.id)}
      onClick={(event) => {
        if (sort?.consumeClick()) return;
        const target = event.target as HTMLElement;
        if (!event.currentTarget.contains(target)) return;
        if (target.closest("button, a, input, select, textarea, [role='checkbox'], [contenteditable='true']") && !target.closest(".td-name-open")) return;
        onOpen();
      }}
    >
      {leave.active ? <span className="td-shine" aria-hidden="true" /> : null}
      {element}
      {ticking.picker}
      <button
        type="button"
        className="td-check"
        role="checkbox"
        aria-checked={todo.done || leave.active}
        aria-label={todo.done ? `Mark "${todo.name}" not done` : `Tick off "${todo.name}"`}
        disabled={!editable || update.isPending || ticking.pending || leave.active}
        onClick={ticking.tick}
      >
        {leave.active ? (
          <>
            <TickBurst />
            <TickMark />
          </>
        ) : todo.done ? (
          <CheckIcon size={13} />
        ) : null}
      </button>

      <span className="td-row-name">
        {todo.pinned ? (
          <span className="td-row-pin" aria-label="Pinned">
            <PinIcon size={13} />
          </span>
        ) : null}
        <button type="button" className="td-name-open" title={`Open "${todo.name}"`}>
          {todo.name}
        </button>
        {workingLabel ? <span className="td-working">{workingLabel}</span> : null}
        {showNotes ? <NoteChips notes={todo.notes} max={2} /> : null}
      </span>

      <Select
        label={`Creator for "${todo.name}"`}
        value={todo.creator}
        options={creatorOptions}
        onChange={(creator) => save({ creator })}
        disabled={!editable}
        searchable
        clearable
        className="td-cell-select"
        renderTrigger={() =>
          todo.creator ? (
            <span className="td-creator">
              <CreatorAvatar name={todo.creator} src={photos(todo.creator)} size={24} />
              <span className="td-creator-name">{todo.creator}</span>
            </span>
          ) : (
            <span className="td-empty-cell">Creator</span>
          )
        }
      />

      <Select
        label={`Who "${todo.name}" is for`}
        value={todo.assignee?.id ?? null}
        options={personOptions}
        onChange={(assignee) => save({ assignee })}
        disabled={!editable}
        clearable
        className="td-cell-select"
        renderTrigger={() => (todo.assignee ? <span className="td-for">{todo.assignee.name}</span> : <span className="td-empty-cell">Who</span>)}
      />

      <Select
        label={`Priority of "${todo.name}"`}
        value={todo.priority}
        options={priorityOptions}
        onChange={(priority) => save({ priority })}
        disabled={!editable}
        clearable
        className="td-cell-select"
        renderTrigger={() =>
          todo.priority ? <span className={`td-pill td-pill-${todo.priority}`}>{PRIORITY_LABEL[todo.priority]}</span> : <span className="td-empty-cell">Priority</span>
        }
      />

      <span className={`td-due ${todo.done ? "" : due?.tone ?? ""}`}>
        <DateTimePicker
          label={`Due date of "${todo.name}"`}
          value={todo.dueOn}
          withTime={false}
          placeholder="Due"
          className="td-cell-select td-date"
          onChange={(iso) => editable && save({ dueOn: iso ? toISODate(new Date(iso)) : null })}
          renderValue={() => (due ? due.text : "Due")}
        />
      </span>

      {telegram ? (
        todo.done ? (
          <span />
        ) : (
          <Tooltip content={mineNow ? "You're working on this" : "Work on this"}>
            <button
              type="button"
              className={`td-work${mineNow ? " on" : ""}`}
              aria-label={mineNow ? `Working on "${todo.name}"` : `Work on "${todo.name}"`}
              aria-pressed={mineNow}
              disabled={mineNow || work.isPending || !telegram.connected}
              onClick={() => work.mutate(todo.id)}
            >
              <PlayIcon size={13} />
            </button>
          </Tooltip>
        )
      ) : null}
      <MenuButton items={menu} label={`More for "${todo.name}"`} className="td-menu">
        <MoreIcon size={16} />
      </MenuButton>
      {shift ? (
        <span className="td-quick" role="group" aria-label={`Actions for "${todo.name}"`}>
          {actions.map((a) => (
            <Tooltip key={a.key} content={a.label}>
              <button type="button" className={`td-quick-button${a.danger ? " danger" : ""}`} aria-label={a.label} onClick={() => void a.run()}>
                {a.icon}
              </button>
            </Tooltip>
          ))}
        </span>
      ) : null}
    </li>
  );
}

/**
 * The to-do open in the panel. It is kept after it leaves the list on screen
 * (ticked off in Open, or given to someone else in Mine), so the panel does
 * not vanish under the hand that changed it, and refreshed while it is there.
 */
export function useOpenTodo(items: Todo[], id: string | null | undefined): Todo | null {
  const [held, setHeld] = useState<Todo | null>(null);
  const found = id ? items.find((t) => t.id === id) ?? null : null;
  useEffect(() => {
    if (found) setHeld(found);
    else if (!id) setHeld(null);
  }, [found, id]);
  return found ?? (held && held.id === id ? held : null);
}
